import React, { useEffect, useMemo, useState } from "react";
import { FiX, FiCheckCircle, FiAlertCircle, FiInfo, FiAlertTriangle } from "react-icons/fi";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import "./AssistantToastStack.scss";

const TOAST_POSITIONS = {
    TOP_CENTER: "top-center",
    TOP_RIGHT: "top-right",
    TOP_LEFT: "top-left",
    BOTTOM_CENTER: "bottom-center",
    BOTTOM_RIGHT: "bottom-right",
    BOTTOM_LEFT: "bottom-left",
};

const ORDERED_POSITIONS = Object.values(TOAST_POSITIONS);
const VALID_POSITIONS = new Set(ORDERED_POSITIONS);

const DEFAULT_APPEARANCE = {
    surfaceOpacity: 0.97,
    compactSurfaceOpacity: 0.95,
    borderOpacity: 0.38,
    compactBorderOpacity: 0.45,
    backdropBlur: 8,
    minWidthPx: 210,
    maxWidthPx: 320,
    compactMinWidthPx: 190,
    compactMaxWidthPx: 300,
};

const clampNumber = (value, min, max, fallback) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return fallback;
    return Math.min(max, Math.max(min, numericValue));
};

const normalizeRgbTriplet = (value) => {
    if (typeof value !== "string") return null;
    const parts = value
        .split(",")
        .map((part) => Number(part.trim()))
        .filter((part) => Number.isFinite(part));
    if (parts.length !== 3) return null;
    return parts.map((part) => clampNumber(part, 0, 255, 0)).join(", ");
};

const resolveCountdownBadge = (toast, nowTimestamp) => {
    if (toast?.isCountdownBadge && Number.isFinite(toast?.expiresAt)) {
        const remainingMs = Math.max(0, toast.expiresAt - nowTimestamp);
        return `${Math.ceil(remainingMs / 1000)}s`;
    }
    return toast?.badge || null;
};

const resolveToastPlacement = (toast, layout) => {
    if (VALID_POSITIONS.has(toast?.placement)) {
        return toast.placement;
    }
    const fallback = toast?.compact
        ? layout.defaultCompactPosition
        : layout.defaultPosition;
    if (VALID_POSITIONS.has(fallback)) {
        return fallback;
    }
    return TOAST_POSITIONS.TOP_CENTER;
};

const buildToastCardStyle = (toast, appearanceDefaults) => {
    const compact = Boolean(toast?.compact);
    const appearance = toast?.appearance || {};
    const defaultSurfaceOpacity = compact
        ? appearanceDefaults.compactSurfaceOpacity
        : appearanceDefaults.surfaceOpacity;
    const defaultBorderOpacity = compact
        ? appearanceDefaults.compactBorderOpacity
        : appearanceDefaults.borderOpacity;

    const style = {
        "--toast-bg-opacity": String(
            clampNumber(appearance?.surfaceOpacity, 0.6, 1, defaultSurfaceOpacity),
        ),
        "--toast-border-opacity": String(
            clampNumber(appearance?.borderOpacity, 0.2, 1, defaultBorderOpacity),
        ),
        "--toast-backdrop-blur": `${clampNumber(
            appearance?.backdropBlur,
            0,
            24,
            appearanceDefaults.backdropBlur,
        )}px`,
    };

    const backgroundRgb = normalizeRgbTriplet(appearance?.backgroundRgb);
    const borderRgb = normalizeRgbTriplet(appearance?.borderRgb);
    if (backgroundRgb) style["--toast-bg-rgb"] = backgroundRgb;
    if (borderRgb) style["--toast-border-rgb"] = borderRgb;
    if (typeof appearance?.textColor === "string") style["--toast-text"] = appearance.textColor;
    if (typeof appearance?.descriptionColor === "string") {
        style["--toast-description"] = appearance.descriptionColor;
    }
    if (typeof appearance?.iconColor === "string") style["--toast-icon"] = appearance.iconColor;
    if (typeof appearance?.shadow === "string") style["--toast-shadow"] = appearance.shadow;

    const minWidthFallback = compact
        ? appearanceDefaults.compactMinWidthPx
        : appearanceDefaults.minWidthPx;
    const maxWidthFallback = compact
        ? appearanceDefaults.compactMaxWidthPx
        : appearanceDefaults.maxWidthPx;
    const minWidthPx = clampNumber(appearance?.minWidthPx, 180, 700, minWidthFallback);
    const maxWidthPx = clampNumber(appearance?.maxWidthPx, 200, 760, maxWidthFallback);
    style.minWidth = `${minWidthPx}px`;
    style.maxWidth = `${maxWidthPx}px`;
    return style;
};

const AssistantToastStack = ({
    toasts = [],
    onClose,
    layout = {
        defaultPosition: TOAST_POSITIONS.TOP_CENTER,
        defaultCompactPosition: TOAST_POSITIONS.TOP_CENTER,
    },
    appearanceDefaults = DEFAULT_APPEARANCE,
}) => {
    const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());
    const hasCountdownToast = useMemo(
        () => toasts.some((toast) => toast?.isCountdownBadge),
        [toasts],
    );

    useEffect(() => {
        if (!hasCountdownToast) return undefined;
        const intervalId = setInterval(() => {
            setNowTimestamp(Date.now());
        }, 250);
        return () => clearInterval(intervalId);
    }, [hasCountdownToast]);

    const typeStyles = {
        [AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.SUCCESS]: {
            icon: <FiCheckCircle />,
            className: "success",
            defaultDescription: AI_ASSISTANT_CONSTANTS.TOASTS.DEFAULT_DESCRIPTIONS.SUCCESS,
        },
        [AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.ERROR]: {
            icon: <FiAlertCircle />,
            className: "error",
            defaultDescription: AI_ASSISTANT_CONSTANTS.TOASTS.DEFAULT_DESCRIPTIONS.ERROR,
        },
        [AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.WARNING]: {
            icon: <FiAlertTriangle />,
            className: "warning",
            defaultDescription: AI_ASSISTANT_CONSTANTS.TOASTS.DEFAULT_DESCRIPTIONS.WARNING,
        },
        [AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.INFO]: {
            icon: <FiInfo />,
            className: "info",
            defaultDescription: AI_ASSISTANT_CONSTANTS.TOASTS.DEFAULT_DESCRIPTIONS.INFO,
        },
    };

    const toastsByPlacement = useMemo(() => {
        return toasts.reduce((acc, toast) => {
            const placement = resolveToastPlacement(toast, layout);
            if (!acc[placement]) {
                acc[placement] = [];
            }
            acc[placement].push(toast);
            return acc;
        }, {});
    }, [layout, toasts]);

    return (
        <>
            {ORDERED_POSITIONS.map((placement) => {
                const group = toastsByPlacement[placement];
                if (!group?.length) return null;
                return (
                    <div
                        className={`chat-toast-stack chat-toast-stack--${placement}`}
                        key={placement}
                    >
                        {group.map((toast) => {
                            const style =
                                typeStyles[toast?.type] ||
                                typeStyles[AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.INFO];
                            const badgeText = resolveCountdownBadge(toast, nowTimestamp);
                            return (
                                <div
                                    key={toast.id}
                                    className={`chat-toast-card ${style.className} ${toast?.compact ? "compact" : ""}`}
                                    style={buildToastCardStyle(toast, appearanceDefaults)}
                                >
                                    <div className="chat-toast-icon">{style.icon}</div>
                                    <div className="chat-toast-content">
                                        <div className="chat-toast-title">{toast.title}</div>
                                        <div className="chat-toast-description">
                                            {toast.description || style.defaultDescription}
                                        </div>
                                    </div>
                                    {badgeText && <span className="chat-toast-badge">{badgeText}</span>}
                                    {toast?.showClose !== false && (
                                        <button
                                            className="chat-toast-close"
                                            type="button"
                                            aria-label={AI_ASSISTANT_CONSTANTS.TOASTS.CLOSE_ARIA_LABEL}
                                            onClick={() => onClose?.(toast.id)}
                                        >
                                            <FiX />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </>
    );
};

export default AssistantToastStack;
